import { useMemo, useState, type ReactNode } from 'react';
import { ChevronRight, Search, X } from 'lucide-react';
import clsx from 'clsx';
import type { LayerBulk, ParamBulkSpec } from '../../core/params/bulk';
import { isVisible, schemaGroups, type Param, type ParamSchema, type ParamValue, type ParamValues } from '../../core/params/types';
import { ParamControl } from './ParamControl';

export interface ParamListProps {
  schema: ParamSchema;
  values: ParamValues;
  bulk: LayerBulk;
  onChange: (key: string, value: ParamValue) => void;
  onBulkChange: (key: string, spec: ParamBulkSpec | undefined) => void;
  onToggleLock: (key: string) => void;
  onRandomize: (key: string) => void;
  search: string;
  /** Slotted between the search field and the groups — for bespoke controls. */
  header?: ReactNode;
  /** Extra content rendered at the end of a named group, e.g. the font picker. */
  groupExtras?: Record<string, ReactNode>;
}

function matches(param: Param, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    param.label.toLowerCase().includes(q) ||
    param.key.toLowerCase().includes(q) ||
    (param.group ?? '').toLowerCase().includes(q) ||
    (param.tooltip ?? '').toLowerCase().includes(q)
  );
}

/**
 * Renders a whole schema: collapsible groups in declaration order, live search across
 * labels, keys, groups and tooltips, and `showIf` filtering so irrelevant controls
 * disappear rather than sit there disabled.
 *
 * While a search is active groups are force-expanded — otherwise a hit inside a closed
 * group would look like no hit at all.
 */
export function ParamList({
  schema,
  values,
  bulk,
  onChange,
  onBulkChange,
  onToggleLock,
  onRandomize,
  search,
  header,
  groupExtras,
}: ParamListProps): JSX.Element {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const groups = useMemo(() => {
    return schemaGroups(schema)
      .map((group) => ({
        name: group.name,
        params: group.params.filter((p) => isVisible(p, values) && matches(p, search)),
      }))
      .filter((group) => group.params.length > 0 || (groupExtras?.[group.name] !== undefined && !search));
  }, [schema, values, search, groupExtras]);

  return (
    <div>
      {header}
      {groups.map((group) => {
        const isOpen = search.length > 0 || !collapsed[group.name];
        return (
          <section key={group.name} className="border-b border-ink-800 last:border-b-0">
            <button
              type="button"
              onClick={() => setCollapsed((c) => ({ ...c, [group.name]: !c[group.name] }))}
              className="flex w-full items-center gap-1.5 py-2.5 text-left transition-colors hover:text-ink-100"
            >
              <ChevronRight
                size={12}
                className={clsx('shrink-0 text-ink-500 transition-transform', isOpen && 'rotate-90')}
              />
              <span className="panel-label flex-1">{group.name}</span>
              <span className="font-mono text-[9px] text-ink-600">{group.params.length}</span>
            </button>
            {isOpen && (
              <div className="pb-3">
                {group.params.map((param) => (
                  <ParamControl
                    key={param.key}
                    param={param}
                    value={values[param.key]}
                    bulk={bulk[param.key]}
                    onChange={(v) => onChange(param.key, v)}
                    onBulkChange={(spec) => onBulkChange(param.key, spec)}
                    onToggleLock={() => onToggleLock(param.key)}
                    onRandomize={() => onRandomize(param.key)}
                  />
                ))}
                {groupExtras?.[group.name]}
              </div>
            )}
          </section>
        );
      })}
      {groups.length === 0 && (
        <p className="py-6 text-center text-[11px] text-ink-500">Keine Parameter passen zu „{search}“.</p>
      )}
    </div>
  );
}

/** Search field shared by the parameter panels. */
export function ParamSearch({ value, onChange }: { value: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-ink-700 bg-ink-850 px-2 py-1.5">
      <Search size={12} className="shrink-0 text-ink-500" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Parameter suchen…"
        spellCheck={false}
        className="w-full bg-transparent text-[11px] text-ink-100 outline-none placeholder:text-ink-500"
      />
      {value && (
        <button type="button" className="ghost-button h-4 w-4 p-0" onClick={() => onChange('')} title="Suche leeren">
          <X size={10} />
        </button>
      )}
    </div>
  );
}
